import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "sc-table-container" }
const _hoisted_2 = {
  key: 0,
  class: "disbursements-table"
}
const _hoisted_3 = {
  key: 1,
  class: "empty-state"
}
const _hoisted_4 = { class: "add-new-container" }
const _hoisted_5 = ["disabled"]
const _hoisted_6 = {
  key: 1,
  class: "limit-message"
}

import { ref, computed, onBeforeUnmount, watch, nextTick } from "vue";
import { Patch, PatchVM } from "@/types/json-patch";
import DisbursementsTableRow from "@/components/v2/disbursements/DisbursementsTableRow.vue";
import {
	DisbursementVM,
	DisbursementFormVM,
	ToggleEvent,
	DisbursementAddedEvent,
	DisbursementBatchSaveRequest,
	DisbursementPatchRequest,
	CreatePostDisbursementChangeRequest,
	AddDisbursementRequest,
	DisbursementChangesSummary
} from "@/types/disbursements";
import DisbursementConfirmModal from '@/components/v2/disbursements/DisbursementConfirmModal.vue';
import { CertificationVM } from "@/types/certifications";
import { $api } from '@/services/api1';
import functions from "@/use/functions.js";
import * as DisbHelpers from '@/composables/disbursements/disbursementHelpers';
import { useDisbursementsStore } from '@/store/store/disbursementsStore';

type RowInstance = InstanceType<typeof DisbursementsTableRow> & {
	saveIfDirty?: () => Promise<void>;
	resetChanges?: () => void;
	getDisbursementId?: () => number;
	getSequenceNumber?: () => string;
	getPatches?: () => Promise<{ patches: Patch[]; patchesVM: PatchVM[] } | null>;s
	validateForm?: () => Promise<boolean>;
	getPendingPostDisbursementChange?: () => Promise<CreatePostDisbursementChangeRequest | null>;
	getFormData?: () => DisbursementFormVM;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'DisbursementsTable',
  props: {
    data: {},
    certification: {},
    isLoading: { type: Boolean },
    editingPostDisbFromHistory: {}
  },
  emits: ["disbursementUpdated", "disbursementAdded", "disbursementRowDeleted", "toggleEdit", "hasDirtyChanged", "batchSaveComplete", "saveCancelled", "saveValidationFailed", "saveFailed", "clearEditRequest"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

const store = useDisbursementsStore();

const emit = __emit;

const totalDisbursements = computed(() => store.disbursements.length);
const hasReachedLimit = computed(() => totalDisbursements.value >= 4);
const canAddNew = computed(() => !props.isLoading && !hasReachedLimit.value);
const isAddDisabled = computed(() => props.isLoading || hasReachedLimit.value || hasDirty.value);
const isSaving = ref(false);
const justSaved = ref(false);
const hasDirty = computed(() => store.hasDirty);

const rowRefs = ref<RowInstance[]>([]);

onBeforeUnmount(() => {
	rowRefs.value = [];
});

const showConfirmModal = ref(false);
const changesSummary = ref<DisbursementChangesSummary[]>([]);

function onRowDirtyChanged({ index, dirty }: { index: number; dirty: boolean }) {	
	if (props.isLoading) {
		return;
	}

	const disbursement = store.disbursements[index];
	if (!disbursement) return;
	
	const disbId = disbursement.details.id;
	
	if (dirty) {
		store.markDirty(disbId);
	} else {
		store.clearDirty(disbId);
	}
	
	emit("hasDirtyChanged", { hasDirty: store.hasDirty });
}

function onPostDisbursementChangeReady() {  
	emit("hasDirtyChanged", { hasDirty: true });
}

async function saveAll() {
	   
	// Step 1: Validate all rows
	for (let index = 0; index < store.disbursements.length; index++) {
		const disbursement = store.disbursements[index];
		if (!store.dirtyIds.has(disbursement.details.id)) continue;
		const row = rowRefs.value[index];
		if (row?.validateForm) {
			const isValid = await row.validateForm();
			if (!isValid) {
				emit('saveValidationFailed');         
				functions.openModal({
					title: 'Validation Error',
					description: `Please fix validation errors before saving.`,
				});
				return;
			}
		}
	}
	
	// Step 2: Collect changes for confirmation
	changesSummary.value = await collectChangesForConfirmation();
	
	if (changesSummary.value.length === 0) {      
		store.clearAllDirty();
		emit("hasDirtyChanged", { hasDirty: false });
		return;
	}

	// Step 3: Validate with backend API
	const batchRequest = await buildBatchRequest(false);
	
	try {
		const validationResponse = await $api.disbursements.ValidateDisbursementBatch(batchRequest);
		
		if (!validationResponse.success || !validationResponse.results) {				
			emit('saveValidationFailed');

			const errorMessage = validationResponse.errors?.length > 0
				? validationResponse.errors.join('<br/>')
				: 'Total disbursement amount exceeds certified amount';
			
			functions.openModal({
				title: 'Validation Error',
				html: errorMessage,
			});
			return;
		}
	} catch (error) {			
		emit('saveValidationFailed')
		functions.openModal({
			title: 'Validation Error',
			description: 'Failed to validate disbursements. Please try again.',
		});
		return;
	}
	
	// Step 4: Show confirmation modal
	showConfirmModal.value = true;
}

async function handleConfirmSave(generateModFile: boolean) {  
	isSaving.value = true;
	try {		
		await performBatchSave(generateModFile);
	} catch (error) {
		showConfirmModal.value = false;
		isSaving.value = false;
		emit('saveFailed');
		functions.openModal({
			title: 'Error',
			description: 'Failed to save changes. Please try again.',
		});
	}
}

function handleCancelConfirm() {
	showConfirmModal.value = false;
	isSaving.value = false;
	changesSummary.value = [];
	emit('saveCancelled');
}

async function buildBatchRequest(generateModFile: boolean): Promise<DisbursementBatchSaveRequest> {
	const updateDisbursements: DisbursementPatchRequest[] = [];
	const createDisbursementRequests: AddDisbursementRequest[] = [];

	for (let index = 0; index < store.disbursements.length; index++) {
		const disbursement = store.disbursements[index];
		if (!store.dirtyIds.has(disbursement.details.id)) continue;
		const row = rowRefs.value[index];
		if (!row || !disbursement) continue;

		const disbursementId = row.getDisbursementId?.() || 0;
		const sequenceNumber = row.getSequenceNumber?.() || '';

		if (disbursement.details.isNew) {
			const formData = row.getFormData?.();
			
			if (formData) {
				const addRequest: AddDisbursementRequest = {
					disbursement: formData,
					certifiedAmount: props.certification.form.certifiedAmount || props.certification.initialFormValues.certifiedAmount,
					certificationSequenceNumber: props.certification.form.sequenceNumber,
					certificationId: props.certification.id,
					loanId: props.certification.loanId,
					appId: props.certification.appId,
					cluid: props.certification.initialFormValues.cluid,
					lenderId: props.certification.header.lenderId,
					productTypeId: props.certification.productTypeId,
					referenceId: props.certification.referenceId,
					borrowerFirstName: props.certification.borrowers.borrower.firstName,
					borrowerLastName: props.certification.borrowers.borrower.lastName,
					disbursementDateValidated: true,
					formOptions: props.certification.disbursementFormOptions,
				};
				
				createDisbursementRequests.push(addRequest);
			}
		} else {			
			const patchData = await row.getPatches?.();
			const postDisbChange = await row.getPendingPostDisbursementChange?.();
						
			if (patchData?.patches || postDisbChange) {				
				const updateRequest: DisbursementPatchRequest = {
					patchDocument: patchData?.patches || [],
					patchesVM: patchData?.patchesVM || [],
					applicationId: props.certification.appId,
					loanId: props.certification.loanId,
					certificationId: props.certification.id,
					disbursementId: disbursementId,
					certificationSequenceNumber: props.certification.initialFormValues.sequenceNumber,
					disbursementSequenceNumber: sequenceNumber,
				};
								
				if (postDisbChange?.data) {					
					updateRequest.postDisbursementChange = postDisbChange.data;
				} else if (postDisbChange?.patches) {					
					updateRequest.postDisbursementChangePatch = postDisbChange.patches;
				}
				
				updateDisbursements.push(updateRequest);
			}
		}
	}
	
	return {
		certificationId: props.certification.id,
		applicationId: props.certification.appId,
		loanId: props.certification.loanId,
		updateDisbursements: updateDisbursements,
		addDisbursements: createDisbursementRequests.length > 0 ? createDisbursementRequests : undefined,
		generateModificationFile: generateModFile,
	};
}

async function performBatchSave(generateModFile: boolean) {	
	const request = await buildBatchRequest(generateModFile);  
		
	if (request.updateDisbursements.length === 0 &&
		(!request.addDisbursements || request.addDisbursements.length === 0)) {
		store.clearAllDirty();
		emit("hasDirtyChanged", { hasDirty: false });
		return;
	}

	const response = await $api.disbursements.SaveDisbursementBatch(request);
		
	if (response.success) {
		justSaved.value = true;
		store.setJustSaved(true);

		emit("batchSaveComplete", response.results);
		await nextTick();		
		for (const row of rowRefs.value) {
			row?.resetChanges?.();
		}
						
		store.clearAllDirty();
		emit("hasDirtyChanged", { hasDirty: false });

		showConfirmModal.value = false;
		isSaving.value = false;

		setTimeout(() => {
			justSaved.value = false;
		}, 1000);
		
		functions.openModal({
			title: 'Success',
			description: 'All changes have been saved successfully.',
		});
	} else {
		showConfirmModal.value = false;
		isSaving.value = false;
		emit('saveFailed');

		let errorMessages = 'Some Changes Failed to Save';
		if (response.errors && response.errors.length > 0)
		{
			errorMessages = response.errors.join('<br/>');
		}

		functions.openModal({
			title: 'Failed',
			html: `<div style="white-space: pre-line; text-align: left;">${errorMessages}</div>`,
		});
	}
}

function resetAll() {
	store.disbursements = store.disbursements.filter(d => !d.details.isNew);
	for (const r of rowRefs.value) {
		r?.resetChanges?.();
	}
	store.clearAllDirty();
	emit("hasDirtyChanged", { hasDirty: false });
}

async function collectChangesForConfirmation(): Promise<DisbursementChangesSummary[]> {
	const summary: DisbursementChangesSummary[] = [];	
	for (let index = 0; index < store.disbursements.length; index++) {
		const disbursement = store.disbursements[index];
		if (!store.dirtyIds.has(disbursement.details.id)) continue;
		const row = rowRefs.value[index];
		if (!row) continue;
		
		const disbursementId = row.getDisbursementId?.();
		const sequenceNumber = row.getSequenceNumber?.();
		if (disbursement.details.isNew) {
			const formData = row.getFormData?.();
			if (formData) {				
				summary.push({
					certSeq: props.certification.form.sequenceNumber,
					disbSeq: sequenceNumber || 'NEW',
					disbursementId: 0,
					isNew: true,
					newDisbursement: {
						status: DisbHelpers.getStatusDisplayName(
							formData.disbursementStatusTypeId,
							props.certification.disbursementFormOptions.disbursementStatusTypes
						),
						amount: DisbHelpers.formatCurrency(formData.disbursementAmount),
						date: DisbHelpers.formatDate(formData.disbursementDate),
						method: DisbHelpers.getMethodDisplayName(
							formData.disbursementMethodTypeId,
							props.certification.disbursementFormOptions.disbursementMethodTypes
						),
					},
				});
			}
			continue;
		}

		const patchData = await row.getPatches?.();
		const postDisbChange = await row.getPendingPostDisbursementChange?.();
		
		if (!patchData && !postDisbChange) {
			continue;
		}
		
		const changeSummary: DisbursementChangesSummary = {
			certSeq: props.certification.form.sequenceNumber,
			disbSeq: sequenceNumber || '',
			disbursementId: disbursementId || 0,
		};
		
		if (patchData?.patches && patchData.patches.length > 0) {
			type FieldChange = {
				field: string;
				previous: string;
				new: string;
			};

			const fieldChanges: FieldChange[] = [];			
			const originalDisbursement = store.disbursements.find(d => d.details.id === disbursementId);
			for (const patch of patchData.patches) {
				if ('value' in patch) {
					const fieldName = patch.path.substring(1);
					const previousValue = originalDisbursement?.details[fieldName];					
					fieldChanges.push({
						field: DisbHelpers.formatFieldName(fieldName),
						previous: DisbHelpers.formatValue(fieldName, previousValue, props.certification.disbursementFormOptions),
						new: DisbHelpers.formatValue(fieldName, patch.value, props.certification.disbursementFormOptions),
					});
				}
			}
			
			if (fieldChanges.length > 0) {
				changeSummary.fieldChanges = fieldChanges;
			}
		}		
		if (postDisbChange) {
			const originalDisbursement = store.disbursements.find(d => d.details.id === disbursementId);
			if (postDisbChange.patches) {
				const postDisbChanges = postDisbChange.patches.patchesVM.map(patchVM => ({
					field: patchVM.label || patchVM.property,
					previous: String(patchVM.from ?? ''),
					new: String(patchVM.to ?? '')
				}));				
				changeSummary.postDisbChange = {
					isEdit: true,
					changes: postDisbChanges,
				};				
			} else if (postDisbChange.data) {								
				changeSummary.postDisbChange = {
					isEdit: false,
					newStatus: DisbHelpers.getStatusDisplayName(
						originalDisbursement?.details.disbursementStatusTypeId,
						props.certification.disbursementFormOptions.disbursementStatusTypes
					),
					newAmount: DisbHelpers.formatCurrency(postDisbChange.data.disbursementAmount),
					processingDate: DisbHelpers.formatDate(postDisbChange.data.processingDate),
					effectiveDate: DisbHelpers.formatDate(postDisbChange.data.effectiveDate),
					settleDate: DisbHelpers.formatDate(postDisbChange.data.settleDate),
					method: DisbHelpers.getMethodDisplayName(
						postDisbChange.data.disbursementMethodTypeId,
						props.certification.disbursementFormOptions.disbursementMethodTypes
					),
					reason: DisbHelpers.getReasonDisplayName(
						postDisbChange.data.postDisbursementChangeReasonTypeId,
						props.certification.disbursementFormOptions.postDisbursementChangeReasonTypes
					),
				};
			}
		}		
		if (changeSummary.fieldChanges || changeSummary.postDisbChange) {
			summary.push(changeSummary);
		}
	}	
	return summary;
}

function handleAddNew() {
	if (hasReachedLimit.value) {
		functions.openModal({
			title: 'Disbursement Limit Reached',
			description: "You've reached the limit of 4 disbursements per certification.",
		});
		return;
	}

	if (hasDirty.value) {
		functions.openModal({
			title: 'Unsaved Changes',
			description: 'Please save or cancel your current changes before adding a new disbursement.',
		});
		return;
	}

	const newSequenceNumber = "New";
	const defaultMethodTypeId = props.certification.disbursementFormOptions.preferredSchoolDisbursementTypeId;
	const defaultMethodTypeName = props.certification.disbursementFormOptions.preferredSchoolDisbursementTypeName;

	const newDisb: DisbursementVM = {
		details: {
			id: -(store.disbursements.length + 1),
			certificationId: props.certification.id,
			sequenceNumber: newSequenceNumber,
			disbursementStatusTypeId: 5, // Scheduled Disbursement
			disbursementMethodTypeId: defaultMethodTypeId,
			certificationDisbursementStatusReasonTypeId: undefined,
			disbursementAmount: 0,
			disbursementDate: '',
			disbursementStatusTypeName: 'Scheduled Disbursement',
			disbursementMethodTypeName: defaultMethodTypeName,
			certificationDisbursementStatusReasonTypeName: null,
			disbursementAmountDisplay: '',
			disbursementDateDisplay: '',
			isEditing: true,
			isDisbursed: false,
			allowPostDisbursementChange: false,
			hasPendingEdits: false,
			hasPendingPostDisbursementChange: false,
			maxAmountForPostDisbursementChange: 0,
			latestPostDisbursementChange: null,
			isNew: true
		},
		form: {
			id: -(store.disbursements.length + 1),
			certificationId: props.certification.id,
			sequenceNumber: newSequenceNumber,
			disbursementStatusTypeId: 5,
			disbursementMethodTypeId: defaultMethodTypeId,
			certificationDisbursementStatusReasonTypeId: null,
			disbursementAmount: null,
			disbursementDate: null,
			disbursementStatusTypeName: 'Scheduled Disbursement',
			disbursementMethodTypeName: defaultMethodTypeName,
			certificationDisbursementStatusReasonTypeName: null,
			disbursementAmountDisplay: null,
			disbursementDateDisplay: null,
			isEditing: true,
			isDisbursed: false,
			allowPostDisbursementChange: false,
			hasPendingEdits: false,
			hasPendingPostDisbursementChange: false,
			maxAmountForPostDisbursementChange: 0,
			latestPostDisbursementChange: null,
			isNew: true
		},
		formOptions: props.certification.disbursementFormOptions,
	};
	store.disbursements.push(newDisb);
}

watch(() => props.editingPostDisbFromHistory, async (editData) => {
	if (!editData) return;
	
	store.setEditRequest(editData);
	const rowIndex = store.disbursements.findIndex(
		d => d.details.id === editData.disbursementId
	);
	
	if (rowIndex !== -1) {			
		const disbursement = store.disbursements[rowIndex];	
		if (disbursement.details.latestPostDisbursementChange &&
				disbursement.details.latestPostDisbursementChange.disbursementAmountLedgerId === editData.postDisbursementChangeAmountLedgerId) {
			if (disbursement.details.disbursementAmount === null || 
					disbursement.details.disbursementAmount === undefined) {					
				functions.openModal({
					title: 'Error',
					description: 'Cannot edit post-disbursement change: disbursement amount is missing.',
				});
				emit('clearEditRequest');
				store.setEditRequest(null);
				return;
			}
			
			const postDisbData: CreatePostDisbursementChangeRequest = {
				disbursementAmount: disbursement.details.disbursementAmount,
				disbursementAmountDisplay: disbursement.details.disbursementAmountDisplay || '',
				postDisbursementChangeReasonTypeId: disbursement.details.latestPostDisbursementChange.postDisbursementChangeReasonTypeId,
				settleDate: disbursement.details.latestPostDisbursementChange.settleDate,
				effectiveDate: disbursement.details.latestPostDisbursementChange.effectiveDate,
				processingDate: disbursement.details.latestPostDisbursementChange.processingDate,
				disbursementMethodTypeId: disbursement.details.latestPostDisbursementChange.disbursementMethodTypeId,
			};
			
			await nextTick();
			const row = rowRefs.value[rowIndex];
			
			if (row?.expandAndOpenPostDisbursementForm) {					
				row.expandAndOpenPostDisbursementForm(
					postDisbData,
					editData.postDisbursementChangeAmountLedgerId
				);

				nextTick(() => {
					const rowElement = document.querySelector(`[data-disbursement-id="${editData.disbursementId}"]`);
					if (rowElement) {
						rowElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
					}
				});
			}
		} else {				
			functions.openModal({
				title: 'Error',
				description: 'Could not find the post-disbursement change data. The page may need to be refreshed.',
			});
		}
	} else {			
		functions.openModal({
			title: 'Error',
			description: 'Could not find the disbursement row.',
		});
	}
	emit('clearEditRequest');
	store.setEditRequest(null);
	
}, { immediate: true });

__expose({ saveAll, resetAll });

const tableHeaders = ref([
	{
		label: "Seq.",
		name: "sequenceNumber",
		title: null,
		key: "sequenceNumber",
		required: false,
		minWidth: "fit-content",
		width: "103px",
	},
	{
		label: "Disb. Status",
		name: "disbursementStatus",
		title: "Disbursement Status",
		key: "disbursementStatusTypeName",
		required: false,
		minWidth: "219px",
		width: "219px",
	},
	{
		label: "Disb. Amount",
		name: "disbursementAmount",
		title: "Disbursement Amount",
		key: "disbursementAmountDisplay",
		required: true,
		width: "90px",
		minWidth: "90px",
	},
	{
		label: "Disb. Date",
		name: "disbursementDate",
		title: "Disbursement Date",
		key: "disbursementDateDisplay",
		required: true,
		width: "90px",
		minWidth: "90px",
	},
	{
		label: "Disb. Method",
		name: "disbursementMethod",
		title: "Disbursement Method",
		key: "disbursementMethodTypeName",
		required: false,
		width: "112px",
		minWidth: "112px",
	},
	{
		label: "",
		name: "",
		title: null,
		key: "",
		required: false,
	},
]);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_unref(store).disbursements.length > 0)
      ? (_openBlock(), _createElementBlock("table", _hoisted_2, [
          _createElementVNode("thead", null, [
            _createElementVNode("tr", null, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tableHeaders.value, (header) => {
                return (_openBlock(), _createElementBlock("th", {
                  key: header.name,
                  style: _normalizeStyle({ minWidth: header.minWidth })
                }, _toDisplayString(header.label), 5))
              }), 128))
            ])
          ]),
          _createElementVNode("tbody", null, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(store).disbursements, (disbursement, index) => {
              return (_openBlock(), _createBlock(DisbursementsTableRow, {
                key: disbursement.form?.id || `new-${index}`,
                ref_for: true,
                ref_key: "rowRefs",
                ref: rowRefs,
                disbursements: _unref(store).disbursements,
                disbursement: disbursement,
                certification: __props.certification,
                index: index,
                onToggleEdit: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('toggleEdit', $event))),
                onHasDirtyChanged: onRowDirtyChanged,
                onPostDisbursementChangeReady: onPostDisbursementChangeReady
              }, null, 8, ["disbursements", "disbursement", "certification", "index"]))
            }), 128))
          ])
        ]))
      : (_openBlock(), _createElementBlock("div", _hoisted_3, [...(_cache[1] || (_cache[1] = [
          _createElementVNode("p", null, "No disbursements found for this certification.", -1)
        ]))])),
    _createElementVNode("div", _hoisted_4, [
      (canAddNew.value)
        ? (_openBlock(), _createElementBlock("button", {
            key: 0,
            class: "add-new-link",
            onClick: handleAddNew,
            disabled: isAddDisabled.value
          }, " + Add New Disbursement ", 8, _hoisted_5))
        : _createCommentVNode("", true),
      (hasReachedLimit.value)
        ? (_openBlock(), _createElementBlock("span", _hoisted_6, " Maximum of 4 disbursements reached "))
        : _createCommentVNode("", true)
    ]),
    (showConfirmModal.value)
      ? (_openBlock(), _createBlock(DisbursementConfirmModal, {
          key: 2,
          changes: changesSummary.value,
          isSaving: isSaving.value,
          canGenerateModificationFile: props.certification.canGenerateModificationFile,
          onConfirm: handleConfirmSave,
          onCancel: handleCancelConfirm
        }, null, 8, ["changes", "isSaving", "canGenerateModificationFile"]))
      : _createCommentVNode("", true)
  ]))
}
}

})